/* eslint-disable react/no-array-index-key */
import { Box, Tab, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';
import type { TapeBuilderNodeAbstract } from '@finley-technologies/tape-builder';

import { ArtifactEditor } from './ArtifactEditor';
import { AssetVerificationTabPanel } from './AssetVerificationTabPanel';
import { NodeTabHeader } from './commonNodeDisplay';
import { TapePanelProps } from './commonTapeNode';
import { DownloadLinkPanel } from './DownloadLinkPanel';
import { FundingVerificationTabPanel } from './FundingVerificationTabPanel';
import { getArtifactInstance, useGraph } from './GraphContext';
import { ValleyBankPanel } from './valleyBank/ValleyBankPanel';

export const getDbNodeProps = (tapeBuilderNode: TapeBuilderNodeAbstract) => {
  finleyAssert(
    TapeBuilderUI.tapeNodes.db_node.isNode(tapeBuilderNode),
    'DbTapePanel must contain a db node',
  );

  const { configuration } = tapeBuilderNode;
  const {
    creditFacilitySlug,
    loanProductSlug,
    type: artifactType,
  } = configuration;
  const isVerificationNode = artifactType === 'fundingVerificationReport';

  const isAssetVerificationNode =
    artifactType === 'receivableDocumentationVerificationReport';

  const isValleyBankIngestNode = artifactType === 'valleyBankIngest';

  return {
    configuration,
    isVerificationNode,
    isAssetVerificationNode,
    isValleyBankIngestNode,
    artifactType,
    creditFacilitySlug,
    loanProductSlug,
  };
};

// panel for DB Nodes.
export const DbTapePanel = (props: TapePanelProps) => {
  const graphContext = useGraph();
  const [tabIndex, setTabIndex] = useState(0);

  const { tapeBuilderNode, mode } = props;

  // when mode changes reset the default tab to 0.
  useEffect(() => {
    setTabIndex(0);
  }, [mode]);

  const artifactInstance = getArtifactInstance({
    tapeBuilderNode,
    graphContext,
  });

  const { downloads, instanceRecordString, jsonRecords } = artifactInstance || {
    downloads: [],
    jsonRecords: [],
    instanceRecordString: JSON.stringify({
      error: 'No artifact instance found',
    }),
  };

  const showAritfactInstancePanel = mode === 'Monitor';

  // instanceRecordString is stringified DbTable.ArtifactInstance.Record
  const { pipelineRunId } = JSON.parse(
    instanceRecordString,
  ) as DbTable.ArtifactInstance.Record;
  const artifactInstanceJson = instanceRecordString;

  const {
    configuration,
    artifactType,
    creditFacilitySlug,
    loanProductSlug,
    isVerificationNode,
    isAssetVerificationNode,
    isValleyBankIngestNode,
  } = getDbNodeProps(tapeBuilderNode);

  const configurationString = JSON.stringify(configuration, null, 4);

  const showVerificationPanel = creditFacilitySlug && isVerificationNode;

  const showValleyBankIngestPanel = isValleyBankIngestNode;

  const showAssetVerificationPanel = !!(
    graphContext.state.adminMode && // asset verification panel is only to be shown for admin dashboard.
    creditFacilitySlug &&
    loanProductSlug &&
    isAssetVerificationNode &&
    pipelineRunId
  );

  return (
    <Tabs
      variant="line"
      width="100%"
      colorScheme="black"
      index={tabIndex}
      onChange={(index) => setTabIndex(index)}
    >
      <NodeTabHeader node={tapeBuilderNode}>
        <Tab>Node Configuration</Tab>
        {showAritfactInstancePanel && <Tab>Artifact Instance</Tab>}
        {downloads.length > 0 && <Tab>Download Links</Tab>}
        {jsonRecords.map((_record, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Tab key={`json-records-${index}`}>
            Instance PG Record {index + 1}
          </Tab>
        ))}
        {showAssetVerificationPanel && <Tab>Asset Verification Report</Tab>}
        {showValleyBankIngestPanel && <Tab>ValleyBankPanel</Tab>}
        {/* @dani verification - remove funding verification approval from tapebuilder when testing is complete */}
        {jsonRecords.map((_record, index) => {
          if (showVerificationPanel) {
            return (
              <Tab key={`approve-${creditFacilitySlug}-${index}`}>
                Approve Funding Verification {index + 1}
              </Tab>
            );
          }
          return <></>;
        })}
      </NodeTabHeader>
      <TabPanels>
        <TabPanel>
          <Box
            width="100%"
            resize="both"
            overflow="auto"
            height={500}
            border="1px solid #ccc"
          >
            <ArtifactEditor
              id={tapeBuilderNode.metadata.tapeBuilderNodeId}
              value={configurationString}
              onChange={() => {}}
              onValidate={() => {}}
              readOnly={mode !== 'Edit'}
            />
          </Box>
        </TabPanel>
        {showAritfactInstancePanel && (
          <TabPanel>
            <Box
              width="100%"
              resize="both"
              overflow="auto"
              height={500}
              border="1px solid #ccc"
            >
              <ArtifactEditor
                id={`${tapeBuilderNode.metadata.tapeBuilderNodeId}-InstanceInfo`}
                value={artifactInstanceJson}
                onChange={() => {}}
                onValidate={() => {}}
                readOnly
              />
            </Box>
          </TabPanel>
        )}

        {downloads.length > 0 && (
          <TabPanel>
            <DownloadLinkPanel downloads={downloads} />
          </TabPanel>
        )}
        {jsonRecords.map((record, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <TabPanel key={`json-records-artifact-editor-${index}`}>
            <Box
              width="100%"
              resize="both"
              overflow="auto"
              height={500}
              border="1px solid #ccc"
            >
              <ArtifactEditor
                id={`${tapeBuilderNode.metadata.tapeBuilderNodeId}-PGRecord`}
                value={record}
                onChange={() => {}}
                onValidate={() => {}}
                readOnly
              />
            </Box>
          </TabPanel>
        ))}
        {showAssetVerificationPanel && (
          <TabPanel height="800px">
            <AssetVerificationTabPanel
              pipelineRunId={pipelineRunId}
              creditFacilitySlug={creditFacilitySlug}
              loanProductSlug={loanProductSlug}
            />
          </TabPanel>
        )}
        {showValleyBankIngestPanel && (
          <TabPanel>
            <ValleyBankPanel configuration={configuration} />
          </TabPanel>
        )}

        {/* @dani verification - remove funding verification approval from tapebuilder when testing is complete */}
        {creditFacilitySlug &&
          artifactType === 'fundingVerificationReport' &&
          isVerificationNode &&
          jsonRecords.map((record, index) => {
            return (
              <TabPanel key={`approve-${creditFacilitySlug}-${index}`}>
                <FundingVerificationTabPanel
                  creditFacilitySlug={creditFacilitySlug}
                  record={record}
                />
              </TabPanel>
            );
          })}
      </TabPanels>
    </Tabs>
  );
};
